import { alpha } from '@mui/material/styles';
import {
  MSI_GEOMETRY,
  MSI_TYPE_SCALE,
} from './msiStyleTokens';
import { getThemeAppStyleTokens, isThemeHighContrast } from './themeTokens';

const controlHeight = (compactMode) => (
  compactMode ? MSI_GEOMETRY.compactControlHeight : MSI_GEOMETRY.controlHeight
);

const semanticTone = (tone, palette, componentColors) => {
  const tones = {
    success: {
      foreground: palette.success,
      background: palette.successBg,
      border: componentColors.semanticBorders.success,
    },
    warning: {
      foreground: palette.warning,
      background: palette.warningBg,
      border: componentColors.semanticBorders.warning,
    },
    error: {
      foreground: palette.danger,
      background: palette.dangerBg,
      border: componentColors.semanticBorders.danger,
    },
    info: {
      foreground: palette.info,
      background: palette.infoBg,
      border: componentColors.semanticBorders.info,
    },
  };
  return tones[tone] || tones.info;
};

const semanticVariants = (component, palette, componentColors) => ['success', 'warning', 'error', 'info'].map((color) => {
  const tone = semanticTone(color, palette, componentColors);
  return {
    props: component === 'alert' ? { severity: color } : { color },
    style: component === 'chip'
      ? {
          color: tone.foreground,
          backgroundColor: tone.background,
          borderColor: tone.border,
        }
      : {
          color: tone.foreground,
          backgroundColor: tone.background,
          border: `1px solid ${tone.border}`,
          '& .MuiAlert-icon': { color: tone.foreground },
        },
  };
});

export function buildMsiMuiComponentOverrides(themeId, compactMode = false) {
  const {
    palette: MSI_PALETTE,
    componentColors: MSI_COMPONENT_COLORS,
    chrome,
    actions,
  } = getThemeAppStyleTokens(themeId);
  const highContrast = isThemeHighContrast(themeId);
  const rowDividerWidth = highContrast ? '0.5px' : '1px';
  const rowDividerColor = highContrast
    ? alpha(MSI_COMPONENT_COLORS.rowDivider, 0.24)
    : MSI_COMPONENT_COLORS.rowDivider;
  const focusVisible = {
    outline: `2px solid ${MSI_COMPONENT_COLORS.focusBorder}`,
    outlineOffset: 2,
  };
  const disabledControl = {
    borderColor: MSI_COMPONENT_COLORS.disabledBorder,
    backgroundColor: MSI_COMPONENT_COLORS.disabledFill,
    color: MSI_COMPONENT_COLORS.disabledText,
  };

  const height = controlHeight(compactMode);
  const verticalPadding = compactMode ? '5px' : '7px';

  return {
    // Loading placeholders: no outline, a faint tint of the text colour (the
    // colour of DotWaveLoader's dots) and a stronger band of it as the wave.
    MuiSkeleton: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(MSI_PALETTE.ink900, 0.04),
          '&::after': {
            background: `linear-gradient(90deg, transparent, ${alpha(MSI_PALETTE.ink900, 0.12)}, transparent)`,
          },
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            '&::after': { animation: 'none' },
          },
        },
      },
    },
    MuiCssBaseline: {
      styleOverrides: {
        html: { backgroundColor: MSI_PALETTE.canvas },
        body: {
          backgroundColor: MSI_PALETTE.canvas,
          color: MSI_PALETTE.ink900,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          color: MSI_PALETTE.ink900,
          backgroundColor: MSI_PALETTE.surface,
          backgroundImage: 'none',
          border: `1px solid ${MSI_PALETTE.line}`,
          borderColor: MSI_PALETTE.line,
          borderRadius: MSI_GEOMETRY.radii.panel,
          boxShadow: MSI_GEOMETRY.cardBoxShadow,
        },
        elevation1: { boxShadow: MSI_GEOMETRY.cardBoxShadow },
        elevation2: { boxShadow: MSI_GEOMETRY.cardBoxShadow },
        outlined: { borderColor: MSI_PALETTE.line },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          minHeight: height,
          padding: `${verticalPadding} 12px`,
          borderRadius: MSI_GEOMETRY.radii.button,
          fontSize: compactMode ? '10.5px' : '11.5px',
          fontWeight: 750,
          lineHeight: 1.1,
          letterSpacing: 0,
          textTransform: 'none',
          boxShadow: 'none',
          '&:focus-visible': focusVisible,
          '&.Mui-disabled': disabledControl,
          '&:hover': { boxShadow: 'none' },
        },
        containedPrimary: {
          border: `1px solid ${MSI_COMPONENT_COLORS.primaryBorder}`,
          backgroundColor: MSI_PALETTE.blue600,
          color: actions.primaryText,
          '&:hover': { backgroundColor: MSI_COMPONENT_COLORS.primaryHover },
        },
        containedError: {
          border: `1px solid ${MSI_COMPONENT_COLORS.dangerActionBorder}`,
          backgroundColor: MSI_COMPONENT_COLORS.dangerAction,
          color: actions.dangerText,
          '&:hover': { backgroundColor: MSI_PALETTE.danger },
        },
        outlined: {
          borderColor: MSI_PALETTE.lineDark,
          backgroundColor: MSI_PALETTE.surface,
          color: MSI_PALETTE.ink700,
          '&:hover': {
            borderColor: MSI_COMPONENT_COLORS.secondaryHoverBorder,
            backgroundColor: MSI_COMPONENT_COLORS.secondaryHover,
            color: MSI_PALETTE.ink900,
          },
        },
        text: {
          color: MSI_PALETTE.blue600,
          '&:hover': { backgroundColor: MSI_PALETTE.blue100 },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          width: height,
          height,
          padding: 0,
          border: `1px solid ${MSI_PALETTE.line}`,
          borderRadius: MSI_GEOMETRY.radii.iconButton,
          backgroundColor: MSI_PALETTE.surface,
          color: MSI_PALETTE.ink500,
          '&:hover': {
            borderColor: MSI_COMPONENT_COLORS.secondaryHoverBorder,
            backgroundColor: MSI_COMPONENT_COLORS.secondaryHover,
            color: MSI_PALETTE.ink900,
          },
          '&:focus-visible': focusVisible,
          '&.Mui-disabled': disabledControl,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          minHeight: height,
          borderRadius: MSI_GEOMETRY.radii.input,
          backgroundColor: MSI_PALETTE.surface,
          color: MSI_PALETTE.ink900,
          fontSize: '12px',
          '& .MuiOutlinedInput-notchedOutline': { borderColor: MSI_PALETTE.lineDark },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: MSI_COMPONENT_COLORS.secondaryHoverBorder,
          },
          '&.Mui-focused': {
            boxShadow: `0 0 0 2px ${MSI_COMPONENT_COLORS.focusRing}`,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: MSI_COMPONENT_COLORS.focusBorder,
            borderWidth: 1,
          },
          '&.Mui-disabled': disabledControl,
          '&.Mui-disabled .MuiOutlinedInput-notchedOutline': {
            borderColor: MSI_COMPONENT_COLORS.disabledBorder,
          },
        },
        input: {
          padding: compactMode ? '5px 9px' : '7px 9px',
          '&::placeholder': { color: MSI_PALETTE.ink400, opacity: 1 },
          '&.Mui-disabled': {
            color: MSI_COMPONENT_COLORS.disabledText,
            WebkitTextFillColor: MSI_COMPONENT_COLORS.disabledText,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: MSI_PALETTE.ink500,
          fontSize: '11px',
          letterSpacing: 0,
          '&.Mui-focused': { color: MSI_PALETTE.blue600 },
          '&.Mui-disabled': { color: MSI_COMPONENT_COLORS.disabledText },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: { minHeight: 'auto', paddingTop: verticalPadding, paddingBottom: verticalPadding },
        icon: { color: MSI_PALETTE.ink500 },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          border: `1px solid ${MSI_PALETTE.line}`,
          borderRadius: MSI_GEOMETRY.radii.panel,
          backgroundColor: MSI_PALETTE.surface,
          boxShadow: '0 8px 24px rgba(12, 23, 38, 0.12)',
        },
        option: {
          fontSize: '11.5px',
          '&[aria-selected="true"]': { backgroundColor: MSI_PALETTE.blue100 },
          '&.Mui-focused': { backgroundColor: MSI_COMPONENT_COLORS.rowHover },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          minHeight: compactMode ? 22 : 24,
          border: `1px solid ${MSI_PALETTE.lineDark}`,
          borderRadius: MSI_GEOMETRY.radii.statusBadge,
          backgroundColor: MSI_PALETTE.surfaceSubtle,
          color: MSI_PALETTE.ink500,
          ...MSI_TYPE_SCALE.statusBadge,
          '&:focus-visible': focusVisible,
          '&.Mui-disabled': { ...disabledControl, opacity: 1 },
        },
      },
      variants: semanticVariants('chip', MSI_PALETTE, MSI_COMPONENT_COLORS),
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: MSI_GEOMETRY.radii.iconButton,
          fontSize: '11px',
          lineHeight: 1.45,
          boxShadow: 'none',
        },
      },
      variants: semanticVariants('alert', MSI_PALETTE, MSI_COMPONENT_COLORS),
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          border: `1px solid ${MSI_PALETTE.line}`,
          borderRadius: 10,
          backgroundColor: MSI_PALETTE.surface,
          backgroundImage: 'none',
          boxShadow: '0 16px 40px rgba(12, 23, 38, 0.12)',
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${MSI_PALETTE.line}`,
          fontSize: MSI_TYPE_SCALE.panelTitle.fontSize,
          fontWeight: MSI_TYPE_SCALE.panelTitle.fontWeight,
          letterSpacing: 0,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${MSI_PALETTE.line}`,
          borderRadius: MSI_GEOMETRY.radii.panel,
          backgroundColor: MSI_PALETTE.surface,
          backgroundImage: 'none',
          boxShadow: '0 8px 24px rgba(12, 23, 38, 0.12)',
        },
        list: { padding: '4px' },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          minHeight: compactMode ? 30 : 34,
          borderRadius: MSI_GEOMETRY.radii.iconButton,
          color: MSI_PALETTE.ink700,
          fontSize: '11.5px',
          '&:hover': { backgroundColor: MSI_COMPONENT_COLORS.rowHover },
          '&.Mui-selected': { backgroundColor: MSI_PALETTE.blue100 },
          '&:focus-visible': focusVisible,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          border: `1px solid ${MSI_PALETTE.line}`,
          borderRadius: MSI_GEOMETRY.radii.panel,
          backgroundColor: MSI_PALETTE.surface,
          backgroundImage: 'none',
          boxShadow: '0 8px 24px rgba(12, 23, 38, 0.12)',
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          border: `1px solid ${chrome.divider}`,
          borderRadius: MSI_GEOMETRY.radii.iconButton,
          backgroundColor: chrome.background,
          color: chrome.primaryText,
          fontSize: '10px',
          boxShadow: '0 6px 16px rgba(12, 23, 38, 0.16)',
        },
        arrow: { color: chrome.background },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          padding: compactMode ? '2px 4px' : '6px 8px',
          borderBottom: `${rowDividerWidth} solid ${rowDividerColor}`,
          color: MSI_PALETTE.ink900,
          ...MSI_TYPE_SCALE.tableBody,
        },
        head: {
          height: MSI_GEOMETRY.tableHeaderHeight,
          borderBottom: `1px solid ${MSI_PALETTE.lineDark}`,
          backgroundColor: MSI_COMPONENT_COLORS.tableHeader,
          color: MSI_PALETTE.ink700,
          ...MSI_TYPE_SCALE.tableHeader,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          backgroundColor: MSI_PALETTE.surface,
          '&:nth-of-type(even)': { backgroundColor: MSI_PALETTE.surfaceSubtle },
          '&:hover': { backgroundColor: MSI_COMPONENT_COLORS.rowHover },
          '&.Mui-selected': {
            backgroundColor: MSI_COMPONENT_COLORS.selectedRow,
            boxShadow: `inset 3px 0 ${MSI_COMPONENT_COLORS.selectedAccent}`,
          },
        },
      },
    },
  };
}
